import React, { useEffect, useRef, useState } from "react";
import { mdiLightbulbOnOutline } from "@mdi/js";
import MdiIcon from "../../../components/device/app/MdiIcon";
import AlertDialog from "../../../components/layout/app/AlertDialog";
import { useToggleTop } from "../../../components/common/ToggleTop";
import { TRAITS } from "../../../constants/devices";
import { sendControl } from "../../../services/deviceControl";
import "../../../assets/styles/app-security.css";

// The gateway's modes, in the app's tab order; the tab index is the value sent.
const MODES = [
  { label: "Arm Away", icon: "/security_device/arm.png" },
  { label: "Stay Arm", icon: "/security_device/stay_home.png" },
  { label: "Disarm", icon: "/security_device/unarm.png" },
];
const DISARM = 2;

const command = (device, id, value) => ({
  on: [device.applianceId],
  off: [],
  action: { [device.applianceId]: { id, trait: Number(TRAITS.SECURITY_MODE), value } },
});

// The gateway's entry in its last state (the app reads the first one).
export const securityStateOf = (device, lastState) =>
  lastState?.devices?.[device.applianceId] ?? Object.values(lastState?.devices ?? {})[0] ?? null;

// The WZLTEG_1 security alarm on its detail page (app/screens/CommonSwichDetails.js
// and app/components/TabBar/AnimatedTabbar.js): Arm Away / Stay Arm / Disarm,
// the SOS emergency call and the electrical-facility switch.
const SecurityAlarmControl = ({ device, lastState }) => {
  const toast = useToggleTop();
  const reported = securityStateOf(device, lastState);
  const [mode, setMode] = useState(null);
  const [sos, setSos] = useState(false);
  const [electro, setElectro] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirmSos, setConfirmSos] = useState(false);
  const sosOn = useRef(false);
  const shown = useRef(device);
  sosOn.current = sos;
  shown.current = device;

  // Follow what the gateway reports.
  useEffect(() => {
    if (reported?.mode !== undefined && reported?.mode !== null) setMode(Number(reported.mode));
  }, [reported?.mode]);
  useEffect(() => {
    if (reported?.electrical_facility !== undefined && reported?.electrical_facility !== null) {
      setElectro(Number(reported.electrical_facility) === 1);
    }
  }, [reported?.electrical_facility]);

  // As in the app, leaving the screen during an emergency call stops it.
  useEffect(
    () => () => {
      if (sosOn.current) sendControl(command(shown.current, 1, DISARM)).catch(() => {});
    },
    []
  );

  const send = async (payload, revert, failure) => {
    setBusy(true);
    try {
      await sendControl(payload);
      return true;
    } catch (error) {
      revert();
      toast(failure, "red");
      return false;
    } finally {
      setBusy(false);
    }
  };

  const chooseMode = (index) => {
    if (busy || index === mode) return;
    const previous = mode;
    const previousSos = sos;
    setMode(index);
    if (index === DISARM) setSos(false);
    send(
      command(device, 1, index),
      () => {
        setMode(previous);
        setSos(previousSos);
      },
      `Couldn't switch ${device.name} to ${MODES[index].label}. Please try again.`
    );
  };

  // The app sends SOS as id 2 and stops it by disarming (id 1, value 2).
  const startSos = async () => {
    setConfirmSos(false);
    setSos(true);
    await send(command(device, 2, 1), () => setSos(false), "Couldn't start the emergency call. Please try again.");
  };

  const stopSos = async () => {
    const previousMode = mode;
    setSos(false);
    setMode(DISARM);
    await send(
      command(device, 1, DISARM),
      () => {
        setSos(true);
        setMode(previousMode);
      },
      "Couldn't stop the emergency call. Please try again."
    );
  };

  const toggleElectro = () => {
    if (busy) return;
    const next = !electro;
    setElectro(next);
    send(command(device, 3, Number(next)), () => setElectro(!next), "Couldn't switch the electrical facility. Please try again.");
  };

  return (
    <div className="hx-sec" style={{ "--hx-sec-notch": "url(/security_device/Subtract.png)" }}>
      {/* Plain buttons, not a radio group: arrow keys must not arm or disarm. */}
      <div className="hx-sec-modes" role="group" aria-label="Security mode">
        {MODES.map((item, index) => {
          const active = mode === index;
          return (
            <button
              key={item.label}
              type="button"
              aria-pressed={active}
              className={`hx-sec-mode ${active ? "is-active" : ""}`}
              onClick={() => chooseMode(index)}
            >
              <span className="hx-sec-mode-icon" aria-hidden="true">
                <span style={{ "--hx-sec-icon": `url(${item.icon})` }} />
              </span>
              <span className="hx-sec-mode-label">{item.label}</span>
            </button>
          );
        })}
      </div>

      <div className="hx-sec-actions">
        <button
          type="button"
          className={`hx-sec-sos ${sos ? "is-active" : ""}`}
          aria-pressed={sos}
          disabled={busy}
          onClick={() => (sos ? stopSos() : setConfirmSos(true))}
        >
          <span className="hx-sec-sos-badge" aria-hidden="true">
            {sos ? <span className="hx-sec-sos-pulse" /> : "SOS"}
          </span>
          <span>{sos ? "Stop Emergency Call" : "Emergency Call"}</span>
        </button>

        <button
          type="button"
          className={`hx-sec-bulb ${electro ? "is-on" : ""}`}
          aria-pressed={electro}
          aria-label="Electrical facility"
          title="Electrical facility"
          disabled={busy}
          onClick={toggleElectro}
        >
          <MdiIcon path={mdiLightbulbOnOutline} size={24} />
        </button>
      </div>

      <AlertDialog
        open={confirmSos}
        type="confirmation"
        title="Emergency Call"
        message={`Start an emergency call from ${device.name}? It sounds the alarm and calls the stored numbers.`}
        onClose={() => setConfirmSos(false)}
        onConfirm={startSos}
      />
    </div>
  );
};

export default SecurityAlarmControl;
